import { problemDocumentSchema } from '@voxler/contracts';
import { env } from './env';
import { supabase } from './supabase';

export class ApiHttpError extends Error {
  constructor(
    readonly status: number,
    /** French `detail` of the problem document (RFC 9457); null when absent or unreadable. */
    readonly detail: string | null = null,
  ) {
    super(`API error ${status}`);
    this.name = 'ApiHttpError';
  }
}

/** The `detail` of a problem+json error body, or null when the body is not a problem document. */
async function readProblemDetail(response: Response): Promise<string | null> {
  try {
    const parsed = problemDocumentSchema.safeParse(await response.json());
    return parsed.success ? parsed.data.detail : null;
  } catch {
    // Not JSON (a proxy error page, an empty body): no detail to show.
    return null;
  }
}

/**
 * The French `detail` of a 422 API error (a business rule or a validation
 * message), to show instead of a generic message; null for anything else.
 */
export function validationDetail(error: unknown): string | null {
  return error instanceof ApiHttpError && error.status === 422 ? error.detail : null;
}

/** Typed fetch against the internal API, attaching the Supabase bearer token. */
export async function internalFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const headers = new Headers(init?.headers);
  if (token) {
    headers.set('authorization', `Bearer ${token}`);
  }
  const response = await fetch(`${env.VITE_API_INTERNAL_URL}${path}`, { ...init, headers });
  if (!response.ok) {
    throw new ApiHttpError(response.status, await readProblemDetail(response));
  }
  // 204 No Content (deletions): no body to read; callers type it `undefined`.
  return (response.status === 204 ? undefined : await response.json()) as T;
}

/** Authenticated download of binary content (files cannot be opened by URL: the API needs the bearer token). */
export async function internalFetchBlob(path: string): Promise<Blob> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const headers = new Headers();
  if (token) {
    headers.set('authorization', `Bearer ${token}`);
  }
  const response = await fetch(`${env.VITE_API_INTERNAL_URL}${path}`, { headers });
  if (!response.ok) {
    throw new ApiHttpError(response.status);
  }
  return response.blob();
}

export interface Operator {
  id: string;
  email: string;
  supabaseUserId: string;
}

export async function internalSend<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  return internalFetch<T>(path, {
    method,
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}
